import { useEffect } from 'react';
import { HeroSection } from './components/HeroSection';
import { ProblemSection } from './components/ProblemSection';
import { SolutionSection } from './components/SolutionSection';
import { RestaurantCallSection } from './components/RestaurantCallSection';
import { RestaurantSection } from './components/RestaurantSection';
import { ContactSection } from './components/ContactSection';
import { FAQSection } from './components/FAQSection';

export const HomePage = () => {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const logoUrl = 'https://static.readdy.ai/image/c76c24f1ef1b9b4d3c3f2ed1c5a01725/772155c272c6560007c86176d4827d96.jpeg';

    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'BrandBoost',
      url: siteUrl,
      logo: logoUrl,
      image: logoUrl,
      description:
        'BrandBoost est une agence de marketing et de visibilité digitale. Nous transformons votre présence en ligne en moteur de croissance : stratégie digitale, création de contenu, acquisition clients et optimisation SEO. Plus de visibilité, plus de clients, plus de ventes.',
      slogan: 'Stratégie · Croissance · Impact',
      foundingDate: '2024',
      areaServed: {
        '@type': 'Country',
        name: 'France',
      },
      knowsLanguage: 'fr-FR',
      serviceType: [
        'Marketing Digital',
        'Stratégie Digitale',
        'SEO & Visibilité',
        'Création de Contenu',
        'Acquisition Clients',
        'Community Management',
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        availableLanguage: 'French',
      },
      sameAs: [siteUrl],
    };

    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'BrandBoost',
      url: siteUrl,
      description: 'Agence de Marketing & Visibilité Digitale - Stratégie, Croissance, Impact',
      inLanguage: 'fr-FR',
      potentialAction: {
        '@type': 'SearchAction',
        target: `${siteUrl}/?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    };

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Quels types d\'entreprises accompagnez-vous ?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Nous accompagnons principalement les TPE, PME et entrepreneurs qui souhaitent développer leur visibilité digitale et leur croissance en ligne, quel que soit leur secteur d\'activité.',
          },
        },
        {
          '@type': 'Question',
          name: 'Quels sont vos services de marketing digital ?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'BrandBoost propose des services de stratégie digitale, création de contenu, optimisation SEO, gestion des réseaux sociaux et acquisition de clients pour booster votre visibilité et vos ventes.',
          },
        },
        {
          '@type': 'Question',
          name: 'Comment BrandBoost transforme-t-il ma présence digitale ?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Nous analysons votre situation actuelle, définissons une stratégie sur mesure, créons du contenu de qualité et mettons en place des actions concrètes pour augmenter votre visibilité, attirer plus de clients et générer plus de ventes.',
          },
        },
        {
          '@type': 'Question',
          name: 'Combien coûtent vos services de visibilité digitale ?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Nos offres sont adaptées à chaque budget. Contactez-nous pour obtenir un devis personnalisé selon vos objectifs de croissance et de stratégie digitale.',
          },
        },
        {
          '@type': 'Question',
          name: 'En combien de temps verrai-je des résultats ?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Les premiers résultats en termes de visibilité digitale et d\'acquisition clients sont généralement visibles dans les 30 à 90 jours selon la stratégie mise en place.',
          },
        },
      ],
    };

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'BrandBoost | Agence Marketing',
      url: siteUrl,
      description:
        'BrandBoost est une agence de marketing et de visibilité digitale. Nous transformons votre présence en ligne en moteur de croissance : stratégie digitale, création de contenu, acquisition clients et optimisation SEO. Plus de visibilité, plus de clients, plus de ventes.',
      inLanguage: 'fr-FR',
      isPartOf: {
        '@type': 'WebSite',
        name: 'BrandBoost',
        url: siteUrl,
      },
      about: {
        '@type': 'Organization',
        name: 'BrandBoost',
      },
      dateModified: '2026-03-20',
    };

    const scripts: HTMLScriptElement[] = [];

    [organizationSchema, websiteSchema, faqSchema, webPageSchema].forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      script.setAttribute('data-schema', 'home');
      document.head.appendChild(script);
      scripts.push(script);
    });

    return () => {
      scripts.forEach((s) => s.remove());
    };
  }, []);

  return (
    <div className="min-h-screen">
      <HeroSection />
      <ProblemSection />
      <SolutionSection />
      <RestaurantCallSection />
      <RestaurantSection />
      <FAQSection />
      <ContactSection />
    </div>
  );
};

// Export par défaut pour le routeur
export default HomePage;
